import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react'
import { useLocation } from 'react-router-dom'
import { format, startOfMonth, startOfQuarter, startOfYear, subDays } from 'date-fns'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Plus, Filter, X, RefreshCw, Search, Settings, Pencil, Trash2 } from 'lucide-react'
import { formatCurrency } from '@/lib/utils'
import api from '@/lib/api'
import type { Transaction, Account, Category } from '@/types'
import { useViewModeStore } from '@/stores/viewModeStore'
import { ConfirmDialog } from '@/components/ConfirmDialog'

type InstitutionLogoProps = {
  institutionId?: string | null
  alt?: string | null
  fallback: string
  className: string
}

function InstitutionLogo({
  institutionId,
  alt,
  fallback,
  className,
}: InstitutionLogoProps) {
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    setHasError(false)
  }, [institutionId])

  if (!institutionId || hasError) {
    return (
      <div className={className}>
        <span>{fallback}</span>
      </div>
    )
  }

  return (
    <div className={className}>
      <img
        src={`/api/institutions/logo/${institutionId}`}
        alt={alt ?? 'Institution logo'}
        className="h-6 w-6 object-contain"
        onError={() => setHasError(true)}
      />
    </div>
  )
}

type OwnerAvatarProps = {
  avatar?: string | null
  nickname?: string | null
  email: string
}

function OwnerAvatar({ avatar, nickname, email }: OwnerAvatarProps) {
  const displayName = nickname || email.split('@')[0]
  const initial = (nickname || email)[0].toUpperCase()

  // Avatar is stored as emoji in DB, not a URL
  if (avatar) {
    return (
      <div
        className="flex h-6 w-6 items-center justify-center text-base"
        title={displayName}
      >
        {avatar}
      </div>
    )
  }

  return (
    <div
      className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-200 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300"
      title={displayName}
    >
      {initial}
    </div>
  )
}

export function TransactionsPage() {
  const location = useLocation()
  const viewMode = useViewModeStore((state) => state.viewMode)
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [accounts, setAccounts] = useState<Account[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [isAddTransactionOpen, setIsAddTransactionOpen] = useState(false)
  const [isSavingTransaction, setIsSavingTransaction] = useState(false)
  const [transactionFormError, setTransactionFormError] = useState<string | null>(null)
  const [activeQuickRange, setActiveQuickRange] = useState<'MTD' | '30D' | 'QTD' | 'YTD' | null>('30D')
  const [transactionForm, setTransactionForm] = useState({
    name: '',
    amount: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    transactionType: 'expense',
    categoryId: '',
    notes: '',
  })

  // Edit transaction modal state
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null)
  const [isEditTransactionOpen, setIsEditTransactionOpen] = useState(false)
  const [editForm, setEditForm] = useState({
    categoryId: '',
    notes: '',
  })
  const [isSavingEdit, setIsSavingEdit] = useState(false)
  const [editFormError, setEditFormError] = useState<string | null>(null)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const [isDeletingTransaction, setIsDeletingTransaction] = useState(false)

  // Category management state
  const [isCategoryManageOpen, setIsCategoryManageOpen] = useState(false)
  const [editingCategory, setEditingCategory] = useState<Category | null>(null)
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    icon: '',
    color: '#6c757d',
    excludeFromSpend: false,
  })
  const [isSavingCategory, setIsSavingCategory] = useState(false)
  const [categoryFormError, setCategoryFormError] = useState<string | null>(null)

  // Filters
  const [startDate, setStartDate] = useState(
    format(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd')
  )
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'))
  const [selectedAccount, setSelectedAccount] = useState<string>('all')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')

  const fetchData = useCallback(async () => {
    console.log('[TransactionsPage] fetchData called with viewMode:', viewMode)
    setIsLoading(true)
    try {
      const [txResponse, accountsResponse, categoriesResponse] = await Promise.all([
        api.get('/transactions', {
          params: {
            start_date: startDate,
            end_date: endDate,
            view: viewMode,
            ...(selectedAccount !== 'all' && { account_id: selectedAccount }),
            ...(selectedCategory !== 'all' && { category_id: selectedCategory }),
          },
        }),
        api.get('/accounts', { params: { view: viewMode } }),
        api.get('/categories'),
      ])
      console.log('[TransactionsPage] Got', txResponse.data.transactions?.length, 'transactions')
      setTransactions(txResponse.data.transactions)
      setAccounts(accountsResponse.data.accounts)
      setCategories(categoriesResponse.data.categories)
    } catch (error) {
      console.error('Failed to fetch transactions:', error)
    } finally {
      setIsLoading(false)
    }
  }, [startDate, endDate, selectedAccount, selectedCategory, viewMode])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const categoryParam = params.get('category_id')
    if (categoryParam) {
      setSelectedCategory(categoryParam)
    }
  }, [location.search])

  const activeCategories = useMemo(
    () => categories.filter((category) => category.is_active ?? true),
    [categories]
  )

  useEffect(() => {
    if (transactionForm.categoryId || activeCategories.length === 0) {
      return
    }
    setTransactionForm((prev) => ({
      ...prev,
      categoryId: String(activeCategories[0].id),
    }))
  }, [activeCategories, transactionForm.categoryId])

  const handleRefresh = async () => {
    setIsRefreshing(true)
    try {
      await api.post('/refresh_transactions')
      await fetchData()
    } catch (error) {
      console.error('Failed to refresh transactions:', error)
    } finally {
      setIsRefreshing(false)
    }
  }

  const clearFilters = () => {
    setStartDate(format(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'))
    setEndDate(format(new Date(), 'yyyy-MM-dd'))
    setSelectedAccount('all')
    setSelectedCategory('all')
    setActiveQuickRange('30D')
  }

  const applyQuickRange = (range: 'MTD' | '30D' | 'QTD' | 'YTD') => {
    const today = new Date()
    const end = format(today, 'yyyy-MM-dd')
    let start = today

    switch (range) {
      case 'MTD':
        start = startOfMonth(today)
        break
      case 'QTD':
        start = startOfQuarter(today)
        break
      case 'YTD':
        start = startOfYear(today)
        break
      case '30D':
      default:
        start = subDays(today, 30)
        break
    }

    setStartDate(format(start, 'yyyy-MM-dd'))
    setEndDate(end)
    setActiveQuickRange(range)
  }

  const resetTransactionForm = () => {
    setTransactionForm({
      name: '',
      amount: '',
      date: format(new Date(), 'yyyy-MM-dd'),
      transactionType: 'expense',
      categoryId: activeCategories.length > 0 ? String(activeCategories[0].id) : '',
      notes: '',
    })
    setTransactionFormError(null)
    setIsSavingTransaction(false)
  }

  const openEditTransaction = (transaction: Transaction) => {
    setSelectedTransaction(transaction)
    setEditForm({
      categoryId: transaction.user_category?.id ? String(transaction.user_category.id) : '',
      notes: transaction.notes || '',
    })
    setEditFormError(null)
    setIsEditTransactionOpen(true)
  }

  const handleEditTransactionSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (!selectedTransaction) return

    setEditFormError(null)
    setIsSavingEdit(true)

    try {
      const promises: Promise<unknown>[] = []

      // Update category if changed
      const newCategoryId = editForm.categoryId ? Number(editForm.categoryId) : null
      const oldCategoryId = selectedTransaction.user_category?.id ?? null
      if (newCategoryId !== oldCategoryId) {
        promises.push(
          api.put(`/transactions/${selectedTransaction.id}/category`, {
            category_id: newCategoryId,
          })
        )
      }

      // Update notes if changed
      if (editForm.notes !== (selectedTransaction.notes || '')) {
        promises.push(
          api.put(`/transactions/${selectedTransaction.id}`, {
            notes: editForm.notes || null,
          })
        )
      }

      if (promises.length > 0) {
        await Promise.all(promises)
        await fetchData()
      }

      setIsEditTransactionOpen(false)
      setSelectedTransaction(null)
    } catch (error) {
      console.error('Failed to update transaction:', error)
      setEditFormError('Unable to update transaction. Please try again.')
    } finally {
      setIsSavingEdit(false)
    }
  }

  const handleDeleteTransaction = async () => {
    if (!selectedTransaction) return

    setIsDeletingTransaction(true)
    try {
      await api.delete(`/transactions/${selectedTransaction.id}`)
      await fetchData()
      setConfirmDeleteOpen(false)
      setIsEditTransactionOpen(false)
      setSelectedTransaction(null)
    } catch (error) {
      console.error('Failed to delete transaction:', error)
      setConfirmDeleteOpen(false)
      setEditFormError('Unable to delete transaction. Please try again.')
    } finally {
      setIsDeletingTransaction(false)
    }
  }

  const handleAddTransactionSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setTransactionFormError(null)

    const amountValue = Number(transactionForm.amount)
    const categoryId = Number(transactionForm.categoryId)
    const nameValue = transactionForm.name.trim()
    const notesValue = transactionForm.notes.trim()

    if (!Number.isFinite(amountValue) || amountValue <= 0) {
      setTransactionFormError('Amount must be greater than zero.')
      return
    }

    if (!transactionForm.date) {
      setTransactionFormError('Date is required.')
      return
    }

    if (!Number.isFinite(categoryId)) {
      setTransactionFormError('Select a category to continue.')
      return
    }

    setIsSavingTransaction(true)

    try {
      await api.post('/transactions', {
        amount: amountValue,
        date: transactionForm.date,
        transaction_type: transactionForm.transactionType,
        user_category_id: categoryId,
        name: nameValue || 'Manual transaction',
        notes: notesValue || undefined,
      })
      await fetchData()
      setIsAddTransactionOpen(false)
      resetTransactionForm()
    } catch (error) {
      console.error('Failed to create transaction:', error)
      setTransactionFormError('Unable to add transaction. Please try again.')
    } finally {
      setIsSavingTransaction(false)
    }
  }

  // Category management handlers
  const resetCategoryForm = () => {
    setCategoryForm({
      name: '',
      icon: '',
      color: '#6c757d',
      excludeFromSpend: false,
    })
    setEditingCategory(null)
    setCategoryFormError(null)
  }

  const startEditCategory = (category: Category) => {
    setEditingCategory(category)
    setCategoryForm({
      name: category.name,
      icon: category.icon,
      color: category.color,
      excludeFromSpend: category.exclude_from_spend,
    })
    setCategoryFormError(null)
  }

  const handleCategorySubmit = async (event: FormEvent) => {
    event.preventDefault()
    setCategoryFormError(null)

    const name = categoryForm.name.trim()
    if (!name) {
      setCategoryFormError('Category name is required.')
      return
    }

    setIsSavingCategory(true)

    try {
      if (editingCategory) {
        // Update existing category
        await api.put(`/categories/${editingCategory.id}`, {
          name,
          icon: categoryForm.icon || undefined,
          color: categoryForm.color,
          exclude_from_spend: categoryForm.excludeFromSpend,
        })
      } else {
        // Create new category
        await api.post('/categories', {
          name,
          icon: categoryForm.icon || undefined,
          color: categoryForm.color,
          exclude_from_spend: categoryForm.excludeFromSpend,
        })
      }

      await fetchData()
      resetCategoryForm()
    } catch (error) {
      console.error('Failed to save category:', error)
      setCategoryFormError('Unable to save category. Please try again.')
    } finally {
      setIsSavingCategory(false)
    }
  }

  const handleDeleteCategory = async (categoryId: number) => {
    if (!confirm('Delete this category? Transactions will be uncategorized.')) {
      return
    }

    try {
      await api.delete(`/categories/${categoryId}`)
      await fetchData()
      if (editingCategory?.id === categoryId) {
        resetCategoryForm()
      }
    } catch (error) {
      console.error('Failed to delete category:', error)
      setCategoryFormError('Unable to delete category.')
    }
  }

  const hasActiveFilters =
    selectedAccount !== 'all' || selectedCategory !== 'all'

  const accountLookup = useMemo(() => {
    const map = new Map<string, Account>()
    accounts.forEach((account) => {
      map.set(account.account_id, account)
    })
    return map
  }, [accounts])

  const filteredTransactions = useMemo(() => {
    let result = transactions

    if (selectedAccount !== 'all') {
      result = result.filter((transaction) => {
        return (
          transaction.account_id === selectedAccount ||
          transaction.account?.id === selectedAccount
        )
      })
    }

    if (selectedCategory !== 'all') {
      result = result.filter(
        (transaction) =>
          transaction.user_category?.id?.toString() === selectedCategory
      )
    }

    const query = searchQuery.trim().toLowerCase()
    if (!query) {
      return result
    }

    const amountQuery = query.replace(/[^0-9.]/g, '')
    const hasAmountQuery = amountQuery.length > 0

    return result.filter((transaction) => {
      if (transaction.name.toLowerCase().includes(query)) {
        return true
      }
      if (hasAmountQuery) {
        const amountText = Math.abs(transaction.amount).toFixed(2)
        return amountText.includes(amountQuery)
      }
      return false
    })
  }, [transactions, searchQuery, selectedAccount, selectedCategory])

  const hasTransactions = transactions.length > 0
  const isSearchActive = searchQuery.trim().length > 0
  const emptyStateMessage =
    hasTransactions && isSearchActive
      ? 'No transactions match your search.'
      : 'No transactions found for the selected filters.'

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-10 w-40" />
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-3 w-24" />
                    </div>
                  </div>
                  <Skeleton className="h-5 w-20" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Dialog
        open={isAddTransactionOpen}
        onOpenChange={(open) => {
          setIsAddTransactionOpen(open)
          if (!open) {
            resetTransactionForm()
          }
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Transaction</DialogTitle>
            <DialogDescription>
              Record a manual income or expense.
            </DialogDescription>
          </DialogHeader>
          <form className="space-y-4" onSubmit={handleAddTransactionSubmit}>
            <div className="grid gap-2">
              <Label htmlFor="transaction-name">Name</Label>
              <Input
                id="transaction-name"
                value={transactionForm.name}
                onChange={(event) =>
                  setTransactionForm((prev) => ({
                    ...prev,
                    name: event.target.value,
                  }))
                }
                placeholder="Whole Foods Market"
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label htmlFor="transaction-amount">Amount</Label>
                <Input
                  id="transaction-amount"
                  type="number"
                  min="0"
                  step="any"
                  value={transactionForm.amount}
                  onChange={(event) =>
                    setTransactionForm((prev) => ({
                      ...prev,
                      amount: event.target.value,
                    }))
                  }
                  placeholder="58.96"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="transaction-date">Date</Label>
                <Input
                  id="transaction-date"
                  type="date"
                  value={transactionForm.date}
                  onChange={(event) =>
                    setTransactionForm((prev) => ({
                      ...prev,
                      date: event.target.value,
                    }))
                  }
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label htmlFor="transaction-type">Type</Label>
                <Select
                  value={transactionForm.transactionType}
                  onValueChange={(value) =>
                    setTransactionForm((prev) => ({
                      ...prev,
                      transactionType: value,
                    }))
                  }
                >
                  <SelectTrigger id="transaction-type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="expense">Expense</SelectItem>
                    <SelectItem value="income">Income</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="transaction-category">Category</Label>
                {activeCategories.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-gray-200 px-3 py-2 text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
                    No categories available.
                  </div>
                ) : (
                  <Select
                    value={transactionForm.categoryId}
                    onValueChange={(value) =>
                      setTransactionForm((prev) => ({
                        ...prev,
                        categoryId: value,
                      }))
                    }
                  >
                    <SelectTrigger id="transaction-category">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {activeCategories.map((category) => (
                        <SelectItem key={category.id} value={String(category.id)}>
                          {category.icon} {category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="transaction-notes">Notes</Label>
              <Input
                id="transaction-notes"
                value={transactionForm.notes}
                onChange={(event) =>
                  setTransactionForm((prev) => ({
                    ...prev,
                    notes: event.target.value,
                  }))
                }
                placeholder="Optional note"
              />
            </div>

            {transactionFormError && (
              <p className="text-sm text-danger">{transactionFormError}</p>
            )}

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsAddTransactionOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSavingTransaction || activeCategories.length === 0}
              >
                {isSavingTransaction ? 'Saving...' : 'Save Transaction'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Transaction Modal */}
      <Dialog
        open={isEditTransactionOpen}
        onOpenChange={(open) => {
          setIsEditTransactionOpen(open)
          if (!open) {
            setSelectedTransaction(null)
            setEditFormError(null)
          }
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Transaction</DialogTitle>
            <DialogDescription>
              {selectedTransaction?.name}
            </DialogDescription>
          </DialogHeader>
          <form className="space-y-4" onSubmit={handleEditTransactionSubmit}>
            <div className="grid gap-2">
              <Label htmlFor="edit-category">Category</Label>
              <Select
                value={editForm.categoryId || 'none'}
                onValueChange={(value) =>
                  setEditForm((prev) => ({
                    ...prev,
                    categoryId: value === 'none' ? '' : value,
                  }))
                }
              >
                <SelectTrigger id="edit-category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No category</SelectItem>
                  {activeCategories.map((category) => (
                    <SelectItem key={category.id} value={String(category.id)}>
                      {category.icon} {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="edit-notes">Notes</Label>
              <Input
                id="edit-notes"
                value={editForm.notes}
                onChange={(event) =>
                  setEditForm((prev) => ({
                    ...prev,
                    notes: event.target.value,
                  }))
                }
                placeholder="Add a note..."
              />
            </div>

            {selectedTransaction?.suggested_category && !editForm.categoryId && (
              <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Suggested category:
                </p>
                <button
                  type="button"
                  className="mt-1 flex items-center gap-2 text-sm font-medium text-primary hover:underline"
                  onClick={() =>
                    setEditForm((prev) => ({
                      ...prev,
                      categoryId: String(selectedTransaction.suggested_category!.id),
                    }))
                  }
                >
                  {selectedTransaction.suggested_category.icon}{' '}
                  {selectedTransaction.suggested_category.name}
                </button>
              </div>
            )}

            {editFormError && (
              <p className="text-sm text-danger">{editFormError}</p>
            )}

            <DialogFooter className="gap-2 sm:justify-between">
              <Button
                type="button"
                variant="ghost"
                className="text-danger hover:text-danger"
                onClick={() => setConfirmDeleteOpen(true)}
                disabled={isSavingEdit || isDeletingTransaction}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </Button>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsEditTransactionOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSavingEdit}>
                  {isSavingEdit ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title="Delete transaction?"
        description="This removes the transaction from your list."
        confirmLabel="Delete"
        destructive
        isConfirming={isDeletingTransaction}
        onConfirm={handleDeleteTransaction}
      />

      {/* Category Management Modal */}
      <Dialog
        open={isCategoryManageOpen}
        onOpenChange={(open) => {
          setIsCategoryManageOpen(open)
          if (!open) {
            resetCategoryForm()
          }
        }}
      >
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Manage Categories</DialogTitle>
            <DialogDescription>
              Create, edit, or delete your expense categories.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-6 sm:grid-cols-2">
            {/* Category List */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Your Categories
              </h3>
              <div className="max-h-64 space-y-1 overflow-y-auto">
                {categories.length === 0 ? (
                  <p className="py-4 text-center text-sm text-gray-500">
                    No categories yet. Create one to get started.
                  </p>
                ) : (
                  categories.map((category) => (
                    <div
                      key={category.id}
                      className={`flex items-center justify-between rounded-lg px-3 py-2 transition-colors ${
                        editingCategory?.id === category.id
                          ? 'bg-primary/10'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className="h-3 w-3 rounded-full"
                          style={{ backgroundColor: category.color }}
                        />
                        <span className="text-sm">
                          {category.icon} {category.name}
                        </span>
                        {category.exclude_from_spend && (
                          <span className="text-xs text-gray-400">(excluded)</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => startEditCategory(category)}
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteCategory(category.id)}
                        >
                          <Trash2 className="h-3 w-3 text-danger" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Category Form */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {editingCategory ? 'Edit Category' : 'New Category'}
              </h3>
              <form onSubmit={handleCategorySubmit} className="space-y-3">
                <div className="grid gap-2">
                  <Label htmlFor="category-name">Name</Label>
                  <Input
                    id="category-name"
                    value={categoryForm.name}
                    onChange={(e) =>
                      setCategoryForm((prev) => ({ ...prev, name: e.target.value }))
                    }
                    placeholder="e.g., Groceries"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="grid gap-2">
                    <Label htmlFor="category-icon">Icon (emoji)</Label>
                    <Input
                      id="category-icon"
                      value={categoryForm.icon}
                      onChange={(e) =>
                        setCategoryForm((prev) => ({ ...prev, icon: e.target.value }))
                      }
                      placeholder="e.g., 🛒"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="category-color">Color</Label>
                    <div className="flex gap-2">
                      <Input
                        id="category-color"
                        type="color"
                        value={categoryForm.color}
                        onChange={(e) =>
                          setCategoryForm((prev) => ({ ...prev, color: e.target.value }))
                        }
                        className="h-9 w-12 cursor-pointer p-1"
                      />
                      <Input
                        value={categoryForm.color}
                        onChange={(e) =>
                          setCategoryForm((prev) => ({ ...prev, color: e.target.value }))
                        }
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="category-exclude"
                    checked={categoryForm.excludeFromSpend}
                    onChange={(e) =>
                      setCategoryForm((prev) => ({
                        ...prev,
                        excludeFromSpend: e.target.checked,
                      }))
                    }
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <Label htmlFor="category-exclude" className="text-sm font-normal">
                    Exclude from spending totals
                  </Label>
                </div>

                {categoryFormError && (
                  <p className="text-sm text-danger">{categoryFormError}</p>
                )}

                <div className="flex gap-2">
                  {editingCategory && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={resetCategoryForm}
                    >
                      Cancel
                    </Button>
                  )}
                  <Button type="submit" disabled={isSavingCategory}>
                    {isSavingCategory
                      ? 'Saving...'
                      : editingCategory
                        ? 'Update Category'
                        : 'Create Category'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Transactions
        </h1>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            Sync
          </Button>
          <Button onClick={() => setIsAddTransactionOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Transaction
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Filters
              </span>
            </div>

            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value)
                  setActiveQuickRange(null)
                }}
                className="w-36"
              />
              <span className="text-gray-500">to</span>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value)
                  setActiveQuickRange(null)
                }}
                className="w-36"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {(['MTD', '30D', 'QTD', 'YTD'] as const).map((range) => (
                <Button
                  key={range}
                  variant={activeQuickRange === range ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => applyQuickRange(range)}
                >
                  {range}
                </Button>
              ))}
            </div>

            <Select value={selectedAccount} onValueChange={setSelectedAccount}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All accounts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All accounts</SelectItem>
                {accounts.map((account) => {
                  const accountLabel = account.custom_name || account.name
                  const fallbackLetter =
                    (account.institution_name || accountLabel || '?')
                      .trim()
                      .charAt(0)
                      .toUpperCase() || '?'

                  return (
                    <SelectItem key={account.id} value={account.account_id}>
                      <span className="flex items-center gap-2">
                        <span aria-hidden="true">
                          <InstitutionLogo
                            institutionId={account.institution_id}
                            alt={account.institution_name || accountLabel}
                            fallback={fallbackLetter}
                            className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-500 dark:bg-gray-800 dark:text-gray-400"
                          />
                        </span>
                        <span className="truncate">{accountLabel}</span>
                      </span>
                    </SelectItem>
                  )
                })}
              </SelectContent>
            </Select>

            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={category.id.toString()}>
                    {category.icon} {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsCategoryManageOpen(true)}
            >
              <Settings className="mr-1 h-4 w-4" />
              Manage
            </Button>

            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="mr-1 h-4 w-4" />
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Transactions List */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="flex items-center gap-2">
              <span>{filteredTransactions.length} transactions</span>
              {isSearchActive && (
                <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                  of {transactions.length}
                </span>
              )}
            </CardTitle>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search transactions"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredTransactions.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-gray-500 dark:text-gray-400">
                {emptyStateMessage}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredTransactions.map((transaction) => {
                const accountInfo =
                  transaction.account || accountLookup.get(transaction.account_id) || null
                const accountName =
                  accountInfo?.name || (transaction.is_manual ? 'Manual Entry' : 'Account')
                const accountMask = accountInfo?.mask ? `••••${accountInfo.mask}` : null
                const accountLabel = accountMask ? `${accountName} ${accountMask}` : accountName
                const metaParts = [
                  format(new Date(transaction.date), 'MMM d, yyyy'),
                  accountLabel,
                ].filter(Boolean)
                const institutionId = accountInfo?.institution_id
                const institutionName = accountInfo?.institution_name || accountName
                const fallbackIcon =
                  transaction.user_category?.icon || (transaction.amount < 0 ? '💸' : '💰')
                const logoWrapperClass = institutionId
                  ? 'flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200'
                  : `flex h-10 w-10 items-center justify-center rounded-full ${
                      transaction.amount < 0
                        ? 'bg-danger-light text-danger dark:bg-danger/10'
                        : 'bg-success-light text-success dark:bg-success/10'
                    }`

                return (
                  <button
                    key={transaction.id}
                    type="button"
                    onClick={() => openEditTransaction(transaction)}
                    className="flex w-full flex-col gap-3 py-4 text-left transition-colors hover:bg-gray-50 sm:flex-row sm:items-center sm:justify-between dark:hover:bg-gray-800/50"
                  >
                    <div className="flex items-center gap-4">
                      {viewMode === 'household' && transaction.owner_email && (
                        <OwnerAvatar
                          avatar={transaction.owner_avatar}
                          nickname={transaction.owner_nickname}
                          email={transaction.owner_email}
                        />
                      )}
                    <InstitutionLogo
                      institutionId={institutionId}
                      alt={institutionName || accountName}
                      fallback={fallbackIcon}
                      className={logoWrapperClass}
                    />
                      <div>
                        <p className="font-medium text-gray-900 dark:text-gray-100">
                          {transaction.name}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {metaParts.join(' • ')}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                      {transaction.user_category && (
                        <Badge
                          variant="secondary"
                          style={{
                            backgroundColor: `${transaction.user_category.color}20`,
                            color: transaction.user_category.color,
                          }}
                        >
                          {transaction.user_category.icon} {transaction.user_category.name}
                        </Badge>
                      )}
                      <span
                        className={`font-semibold ${
                          transaction.amount < 0 ? 'text-danger' : 'text-success'
                        }`}
                      >
                        {transaction.amount < 0 ? '-' : '+'}
                        {formatCurrency(Math.abs(transaction.amount))}
                      </span>
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
